<template>
  <div ref="formRoot" class="ad-form">
    <div class="ad-section">
      <div class="ad-section-header">
        <Icon icon="mdi:domain" width="16" color="#888" />
        <span>Company</span>
        <div v-if="sectionDocNames.length" class="ad-section-docs">
          <span class="ad-section-docs-label">Applies to:</span>
          <v-chip
            v-for="(name, i) in sectionDocNames.slice(0, MAX_DOC_CHIPS)"
            :key="'company-doc-' + i"
            size="x-small"
            variant="tonal"
            color="primary"
            class="ad-doc-chip"
          >
            {{ name }}
          </v-chip>
          <span v-if="sectionDocNames.length > MAX_DOC_CHIPS" class="ad-more-chip">
            +{{ sectionDocNames.length - MAX_DOC_CHIPS }}
          </span>
        </div>
      </div>
      <v-row dense>
        <v-col cols="12" md="8">
          <v-text-field
            v-model="model.companyName"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="hasRequiredError('companyName')"
            data-required-key="companyName"
            class="ad-required-field"
          >
            <template #label>Company name <span class="ad-required-mark">*</span></template>
          </v-text-field>
        </v-col>
      </v-row>
    </div>

    <div class="ad-section">
      <div class="ad-section-header">
        <Icon icon="mdi:file-document-outline" width="16" color="#888" />
        <span>Notice</span>
        <div v-if="sectionDocNames.length" class="ad-section-docs">
          <span class="ad-section-docs-label">Applies to:</span>
          <v-chip
            v-for="(name, i) in sectionDocNames.slice(0, MAX_DOC_CHIPS)"
            :key="'notice-doc-' + i"
            size="x-small"
            variant="tonal"
            color="primary"
            class="ad-doc-chip"
          >
            {{ name }}
          </v-chip>
        </div>
      </div>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-menu
            v-model="noticeDateMenu"
            :close-on-content-click="false"
            location="bottom"
            :disabled="!!readOnly"
          >
            <template #activator="{ props: menuProps }">
              <v-text-field
                v-bind="menuProps"
                v-model="noticeDateDisplay"
                :placeholder="noticeDatePlaceholder"
                variant="outlined"
                density="compact"
                hide-details="auto"
                readonly
                append-inner-icon="mdi-calendar"
                :disabled="!!readOnly"
                :error="hasRequiredError('noticeDate')"
                data-required-key="noticeDate"
                class="ad-required-field"
              >
                <template #label>Notice date <span class="ad-required-mark">*</span></template>
              </v-text-field>
            </template>
            <v-date-picker
              v-bind="noticeDatePickerProps"
              v-model="noticeDatePickerValue"
              @update:model-value="noticeDateMenu = false"
            />
          </v-menu>
          <v-chip
            v-if="shouldShowDateSuggestion('noticeDate', suggestedToday)"
            size="x-small"
            variant="outlined"
            color="warning"
            prepend-icon="mdi-lightbulb-outline"
            closable
            close-icon="mdi-close"
            class="mt-1 ad-suggest-chip"
            @mousedown.stop.prevent
            @click.stop.prevent="applySuggestedDate('noticeDate', suggestedToday)"
            @click:close.stop.prevent="dismissSuggestion('noticeDate', suggestedToday)"
          >
            Today: {{ formatIsoForSession(suggestedToday) }}
          </v-chip>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="model.noticeRecipientName"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="hasRequiredError('noticeRecipientName')"
            data-required-key="noticeRecipientName"
            class="ad-required-field"
          >
            <template #label>Notice recipient name <span class="ad-required-mark">*</span></template>
            <template #append-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tProps }">
                  <v-btn
                    v-bind="tProps"
                    icon="mdi-creation"
                    size="x-small"
                    variant="text"
                    color="primary"
                    :disabled="!!readOnly"
                    @click.stop="requestNoticeRecipientLookup"
                  />
                </template>
                <span>Lookup global contact from chat</span>
              </v-tooltip>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-text-field
            v-model="model.replyToAddress"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="hasRequiredError('replyToAddress')"
            data-required-key="replyToAddress"
            class="ad-required-field"
          >
            <template #label>Reply-to address <span class="ad-required-mark">*</span></template>
            <template #append-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tProps }">
                  <v-btn
                    v-bind="tProps"
                    icon="mdi-creation"
                    size="x-small"
                    variant="text"
                    color="primary"
                    :disabled="!!readOnly"
                    @click.stop="requestReplyToAddressInput"
                  />
                </template>
                <span>Enter Singapore address in chat</span>
              </v-tooltip>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="model.noticeSignerName"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="hasRequiredError('noticeSignerName')"
            data-required-key="noticeSignerName"
            class="ad-required-field"
          >
            <template #label>Notice signer name <span class="ad-required-mark">*</span></template>
            <template #append-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tProps }">
                  <v-btn
                    v-bind="tProps"
                    icon="mdi-creation"
                    size="x-small"
                    variant="text"
                    color="primary"
                    :disabled="!!readOnly"
                    @click.stop="requestNoticeSignerLookup"
                  />
                </template>
                <span>Lookup company secretary from chat</span>
              </v-tooltip>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
    </div>

    <div class="ad-section">
      <div class="ad-section-header">
        <Icon icon="mdi:account-tie" width="16" color="#888" />
        <span>Individual Controller</span>
        <div v-if="sectionDocNames.length" class="ad-section-docs">
          <span class="ad-section-docs-label">Applies to:</span>
          <v-chip
            v-for="(name, i) in sectionDocNames.slice(0, MAX_DOC_CHIPS)"
            :key="'controller-doc-' + i"
            size="x-small"
            variant="tonal"
            color="primary"
            class="ad-doc-chip"
          >
            {{ name }}
          </v-chip>
        </div>
      </div>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="model.controllerFullName"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="hasRequiredError('controllerFullName')"
            data-required-key="controllerFullName"
            class="ad-required-field"
          >
            <template #label>Controller full name <span class="ad-required-mark">*</span></template>
            <template #append-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tProps }">
                  <v-btn
                    v-bind="tProps"
                    icon="mdi-creation"
                    size="x-small"
                    variant="text"
                    color="primary"
                    :disabled="!!readOnly"
                    @click.stop="requestControllerLookup"
                  />
                </template>
                <span>Lookup contact from chat</span>
              </v-tooltip>
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="model.controllerAliases"
            label="Aliases (optional)"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
          />
        </v-col>
      </v-row>
      <NoticeIndividualProfileFields
        :model="model"
        :read-only="!!readOnly"
        @request-address-lookup="(ctx) => emit('request-address-lookup', ctx)"
      />
      <v-row dense class="ad-profile-meta-row">
        <v-col cols="12" md="4">
          <CmBaseCodeAutocomplete
            v-model="model.controllerNationality"
            main-code="B04"
            label="Nationality"
            :read-only="!!readOnly"
            :error="hasRequiredError('controllerNationality')"
            data-required-key="controllerNationality"
            required-mark
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-menu
            v-model="dobMenu"
            :close-on-content-click="false"
            location="bottom"
            :disabled="!!readOnly"
          >
            <template #activator="{ props: menuProps }">
              <v-text-field
                v-bind="menuProps"
                v-model="dobDisplay"
                :placeholder="dobPlaceholder"
                variant="outlined"
                density="compact"
                hide-details="auto"
                readonly
                append-inner-icon="mdi-calendar"
                :disabled="!!readOnly"
                :error="hasRequiredError('controllerDateOfBirth')"
                data-required-key="controllerDateOfBirth"
                class="ad-required-field"
              >
                <template #label>Date of birth <span class="ad-required-mark">*</span></template>
              </v-text-field>
            </template>
            <v-date-picker
              v-bind="dobPickerProps"
              v-model="dobPickerValue"
              @update:model-value="dobMenu = false"
            />
          </v-menu>
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="model.controllerEmail"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="hasRequiredError('controllerEmail')"
            data-required-key="controllerEmail"
            class="ad-required-field"
          >
            <template #label>Email <span class="ad-required-mark">*</span></template>
          </v-text-field>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="model.controllerIdentificationNo"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="hasRequiredError('controllerIdentificationNo')"
            data-required-key="controllerIdentificationNo"
            class="ad-required-field"
          >
            <template #label>NRIC / FIN / Passport no <span class="ad-required-mark">*</span></template>
          </v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <v-menu
            v-model="becameDateMenu"
            :close-on-content-click="false"
            location="bottom"
            :disabled="!!readOnly"
          >
            <template #activator="{ props: menuProps }">
              <v-text-field
                v-bind="menuProps"
                v-model="becameDateDisplay"
                :placeholder="becameDatePlaceholder"
                variant="outlined"
                density="compact"
                hide-details="auto"
                readonly
                append-inner-icon="mdi-calendar"
                :disabled="!!readOnly"
                :error="hasRequiredError('controllerBecameDate')"
                data-required-key="controllerBecameDate"
                class="ad-required-field"
              >
                <template #label>Date became controller <span class="ad-required-mark">*</span></template>
              </v-text-field>
            </template>
            <v-date-picker
              v-bind="becameDatePickerProps"
              v-model="becameDatePickerValue"
              @update:model-value="becameDateMenu = false"
            />
          </v-menu>
          <v-chip
            v-if="shouldShowDateSuggestion('controllerBecameDate', suggestedToday)"
            size="x-small"
            variant="outlined"
            color="warning"
            prepend-icon="mdi-lightbulb-outline"
            closable
            close-icon="mdi-close"
            class="mt-1 ad-suggest-chip"
            @mousedown.stop.prevent
            @click.stop.prevent="applySuggestedDate('controllerBecameDate', suggestedToday)"
            @click:close.stop.prevent="dismissSuggestion('controllerBecameDate', suggestedToday)"
          >
            Today: {{ formatIsoForSession(suggestedToday) }}
          </v-chip>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { Icon } from '@iconify/vue';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import {
  getCurrentDateByJurisdiction,
  useDateField,
  useFormatDateByUserFormat,
} from '@/shared/composables/dateUtils';
import type { NoticeIndividualFormModel } from './composables/useFormPayload';
import NoticeIndividualProfileFields from './NoticeIndividualProfileFields.vue';
import CmBaseCodeAutocomplete from '../../shared/CmBaseCodeAutocomplete.vue';
import { getB04Options, resolveCmBaseCodeValue } from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';

const MAX_DOC_CHIPS = 3;
const DEFAULT_DOC = 'Notice for Controllers 386AG(2)(a) (Individual).docx';

type NoticeDateField = 'noticeDate' | 'controllerBecameDate';

const props = withDefaults(
  defineProps<{
    model: NoticeIndividualFormModel;
    entityCd?: string;
    readOnly?: boolean;
    documents?: Array<{ templateName?: string; docCode?: string }>;
    selectedPerson?: OfficerDetail | null;
    activePersonContext?: Record<string, unknown> | null;
  }>(),
  {
    entityCd: '',
    readOnly: false,
    documents: () => [],
    selectedPerson: null,
    activePersonContext: null,
  },
);

const emit = defineEmits<{
  (e: 'request-person-lookup', context: Record<string, unknown>): void;
  (e: 'request-address-lookup', context: Record<string, unknown>): void;
}>();

const formRoot = ref<HTMLElement | null>(null);
const requiredErrorKeys = ref<Set<string>>(new Set());

function hasRequiredError(key: string): boolean {
  return requiredErrorKeys.value.has(key);
}

function setRequiredErrors(keys: string[]) {
  requiredErrorKeys.value = new Set(keys);
}

function firstMissingRequiredKeys(): string[] {
  const missing: string[] = [];
  const pushIfEmpty = (key: string, value: unknown) => {
    if (!trim(value)) missing.push(key);
  };

  pushIfEmpty('companyName', props.model.companyName);
  pushIfEmpty('noticeDate', props.model.noticeDate);
  pushIfEmpty(
    'noticeRecipientName',
    trim(props.model.noticeRecipientName) || trim(props.model.controllerFullName),
  );
  pushIfEmpty('replyToAddress', props.model.replyToAddress);
  pushIfEmpty('noticeSignerName', props.model.noticeSignerName);
  pushIfEmpty('controllerFullName', props.model.controllerFullName);
  pushIfEmpty('controllerResidentialAddress', props.model.controllerResidentialAddress);
  pushIfEmpty('controllerNationality', props.model.controllerNationality);
  pushIfEmpty('controllerDateOfBirth', props.model.controllerDateOfBirth);
  pushIfEmpty('controllerEmail', props.model.controllerEmail);
  pushIfEmpty('controllerContactNo', props.model.controllerContactNo);
  pushIfEmpty('controllerMobileCountryCd', props.model.controllerMobileCountryCd);
  pushIfEmpty('controllerIdentificationNo', props.model.controllerIdentificationNo);
  pushIfEmpty('controllerBecameDate', props.model.controllerBecameDate);

  return missing;
}

async function focusRequiredField(key: string): Promise<void> {
  await nextTick();
  const root = formRoot.value;
  if (!root) return;
  const target = root.querySelector<HTMLElement>(`[data-required-key="${key}"]`);
  if (!target) return;
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const focusable = target.querySelector<HTMLElement>('input, textarea, [role="combobox"], button');
  focusable?.focus();
}

async function validateAndFocusRequiredFields(): Promise<boolean> {
  const missingKeys = firstMissingRequiredKeys();
  setRequiredErrors(missingKeys);
  if (missingKeys.length === 0) return true;
  await focusRequiredField(missingKeys[0]);
  return false;
}

defineExpose({
  validateAndFocusRequiredFields,
});

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

const sectionDocNames = computed(() => {
  const names = (props.documents || [])
    .map((d) => trim(d.templateName))
    .filter(Boolean);
  return names.length ? names : [DEFAULT_DOC];
});

const formatDateByUserFormat = useFormatDateByUserFormat();
const suggestedToday = computed(() => getCurrentDateByJurisdiction());
const dismissedSuggestions = ref<Set<string>>(new Set());

function normalizeIso(v: unknown): string {
  return trim(v);
}

function bindModelDate(
  get: () => string,
  set: (v: string) => void,
  field: ReturnType<typeof useDateField>,
) {
  watch(get, (v) => {
    const s = normalizeIso(v);
    if (field.dateValue.value !== s) field.dateValue.value = s;
  }, { immediate: true });
  watch(field.dateValue, (v) => {
    const s = normalizeIso(v);
    if (normalizeIso(get()) !== s) set(s);
  });
}

const noticeDate = useDateField();
bindModelDate(
  () => props.model.noticeDate,
  (v) => { props.model.noticeDate = v; },
  noticeDate,
);
const noticeDateMenu = ref(false);
const noticeDateDisplay = noticeDate.dateDisplay;
const noticeDatePickerValue = noticeDate.datePickerValue;
const noticeDatePlaceholder = noticeDate.datePlaceholder;
const noticeDatePickerProps = noticeDate.datePickerProps;

const dob = useDateField();
bindModelDate(
  () => props.model.controllerDateOfBirth,
  (v) => { props.model.controllerDateOfBirth = v; },
  dob,
);
const dobMenu = ref(false);
const dobDisplay = dob.dateDisplay;
const dobPickerValue = dob.datePickerValue;
const dobPlaceholder = dob.datePlaceholder;
const dobPickerProps = dob.datePickerProps;

const becameDate = useDateField();
bindModelDate(
  () => props.model.controllerBecameDate,
  (v) => { props.model.controllerBecameDate = v; },
  becameDate,
);
const becameDateMenu = ref(false);
const becameDateDisplay = becameDate.dateDisplay;
const becameDatePickerValue = becameDate.datePickerValue;
const becameDatePlaceholder = becameDate.datePlaceholder;
const becameDatePickerProps = becameDate.datePickerProps;

function suggestionKey(field: NoticeDateField, value: string): string {
  return `${field}:${value}`;
}

function currentDateValue(field: NoticeDateField): string {
  if (field === 'noticeDate') return trim(props.model.noticeDate);
  if (field === 'controllerBecameDate') return trim(props.model.controllerBecameDate);
  return '';
}

function isSuggestionDismissed(field: NoticeDateField, value: string): boolean {
  if (!value) return true;
  return dismissedSuggestions.value.has(suggestionKey(field, value));
}

function dismissSuggestion(field: NoticeDateField, value: string) {
  if (!value) return;
  const next = new Set(dismissedSuggestions.value);
  next.add(suggestionKey(field, value));
  dismissedSuggestions.value = next;
}

function shouldShowDateSuggestion(field: NoticeDateField, value: string): boolean {
  if (!value) return false;
  return currentDateValue(field) !== value && !isSuggestionDismissed(field, value);
}

function applySuggestedDate(field: NoticeDateField, value: string) {
  if (!value) return;
  if (field === 'noticeDate') {
    props.model.noticeDate = value;
    noticeDate.dateValue.value = value;
  } else if (field === 'controllerBecameDate') {
    props.model.controllerBecameDate = value;
    becameDate.dateValue.value = value;
  }
}

function formatIsoForSession(iso: string): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0);
  if (isNaN(d.getTime())) return iso;
  return formatDateByUserFormat(d);
}

function requestNoticeRecipientLookup() {
  if (props.readOnly) return;
  emit('request-person-lookup', {
    fieldGroup: 'noticeRecipient',
    defaultRole: 'CONTACT',
  });
}

function requestControllerLookup() {
  if (props.readOnly) return;
  emit('request-person-lookup', {
    fieldGroup: 'individualController',
    defaultRole: 'CONTACT',
  });
}

function requestNoticeSignerLookup() {
  if (props.readOnly) return;
  emit('request-person-lookup', {
    fieldGroup: 'noticeSigner',
    defaultRole: 'SECRETARY',
  });
}

function requestReplyToAddressInput() {
  if (props.readOnly) return;
  emit('request-address-lookup', {
    fieldGroup: 'replyToAddress',
    index: 0,
    addresses: [],
    directInput: true,
    subjectLabel: 'Reply-to address',
  });
}

function resolveContactRegNo(person: OfficerDetail): string {
  const raw = person as unknown as Record<string, unknown>;
  return trim(
    raw.CONTACT_REG_NO
    ?? raw.contactRegNo
    ?? raw.contact_reg_no
    ?? person.contactRegNo
    ?? raw.regNo
    ?? '',
  );
}

function resolvePersonAddress(person: OfficerDetail): string {
  const raw = person as unknown as Record<string, unknown>;
  const direct = trim(raw.primaryAddress ?? raw.address ?? raw.residentialAddress);
  if (direct) return direct;
  const block = trim(raw.blockNo);
  const street = trim(raw.streetName);
  const postal = trim(raw.postalCode);
  const line = [block, street].filter(Boolean).join(' ').trim();
  if (line && postal) return `${line}, SINGAPORE ${postal}`;
  return line;
}

function resolvePersonAlternateAddress(person: OfficerDetail): string {
  const raw = person as unknown as Record<string, unknown>;
  const direct = trim(raw.alternateAddress ?? raw.altAddress ?? raw.secondaryAddress);
  if (direct) return direct;
  const block = trim(raw.altBlockNo ?? raw.ALT_BLOCK_NO);
  const street = trim(raw.altStreetName ?? raw.ALT_STREET_NAME);
  const postal = trim(raw.altPostalCode ?? raw.ALT_POSTAL_CODE);
  const line = [block, street].filter(Boolean).join(' ').trim();
  if (line && postal) return `${line}, SINGAPORE ${postal}`;
  return line;
}

function resolveNationalityFromPerson(raw: Record<string, unknown>): string {
  const natCd = trim(raw.NATIONALITY_CD ?? raw.nationalityCd);
  if (natCd) return natCd;
  const nationality = trim(raw.nationalityName ?? raw.nationality);
  if (!nationality) return '';
  return resolveCmBaseCodeValue(nationality, getB04Options());
}

function applySelectedPerson(person: OfficerDetail) {
  const raw = person as unknown as Record<string, unknown>;
  const fullName = trim(person.name ?? person.contactName);
  const idValue = resolveContactRegNo(person);
  const nationality = resolveNationalityFromPerson(raw);
  const dobVal = trim(raw.dateOfBirth);
  const email = trim(raw.contactEmail);
  const mobile = trim(raw.mobileNo);
  const idTypeCd = trim(raw.idTypeCd ?? raw.ID_TYPE_CD);
  const countryCd = trim(raw.countryCd ?? raw.COUNTRY_CD);
  const areaCode = trim(raw.areaCode ?? raw.AREA_CODE);
  const address = resolvePersonAddress(person);
  const alternateAddress = resolvePersonAlternateAddress(person);

  if (fullName) {
    props.model.controllerFullName = fullName;
    props.model.noticeRecipientName = fullName;
  }
  if (idValue) props.model.controllerIdentificationNo = idValue;
  if (idTypeCd) props.model.controllerIdTypeCd = idTypeCd;
  if (countryCd) props.model.controllerMobileCountryCd = countryCd;
  if (areaCode) props.model.controllerMobileAreaCode = areaCode;
  if (nationality) props.model.controllerNationality = nationality;
  if (dobVal) props.model.controllerDateOfBirth = dobVal;
  if (email) props.model.controllerEmail = email;
  if (mobile) props.model.controllerContactNo = mobile;
  if (address) props.model.controllerResidentialAddress = address;
  if (alternateAddress) props.model.controllerAlternateAddress = alternateAddress;
  props.model.contactCd = trim(person.contactCd);
}

function applySelectedSecretary(person: OfficerDetail) {
  const fullName = trim(person.name ?? person.contactName);
  if (fullName) props.model.noticeSignerName = fullName;
}

async function hydrateContactRegNoIfMissing(person: OfficerDetail) {
  if (trim(props.model.controllerIdentificationNo)) return;
  const entityCd = trim(props.entityCd);
  const contactCd = trim(person.contactCd);
  if (!entityCd || !contactCd) return;
  try {
    const response: unknown = await docGenApi.getContactDetail(entityCd, contactCd);
    const detail = (response as { data?: { data?: Record<string, unknown> } })?.data?.data
      ?? (response as { data?: Record<string, unknown> })?.data
      ?? response;
    if (!detail || typeof detail !== 'object') return;
    const regNo = trim(
      (detail as Record<string, unknown>).CONTACT_REG_NO
      ?? (detail as Record<string, unknown>).contactRegNo
      ?? (detail as Record<string, unknown>).contact_reg_no,
    );
    if (regNo) props.model.controllerIdentificationNo = regNo;
    const idType = trim(
      (detail as Record<string, unknown>).ID_TYPE_CD
      ?? (detail as Record<string, unknown>).idTypeCd,
    );
    if (idType && !trim(props.model.controllerIdTypeCd)) props.model.controllerIdTypeCd = idType;
    const country = trim(
      (detail as Record<string, unknown>).COUNTRY_CD
      ?? (detail as Record<string, unknown>).countryCd,
    );
    if (country && !trim(props.model.controllerMobileCountryCd)) {
      props.model.controllerMobileCountryCd = country;
    }
    const area = trim(
      (detail as Record<string, unknown>).AREA_CODE
      ?? (detail as Record<string, unknown>).areaCode,
    );
    if (area && !trim(props.model.controllerMobileAreaCode)) props.model.controllerMobileAreaCode = area;
    if (!trim(props.model.controllerAlternateAddress)) {
      const altLine1 = trim(
        (detail as Record<string, unknown>).ALT_FOREIGN_ADDRESS_LINE_1
        ?? (detail as Record<string, unknown>).altForeignAddressLine1,
      );
      const altLine2 = trim(
        (detail as Record<string, unknown>).ALT_FOREIGN_ADDRESS_LINE_2
        ?? (detail as Record<string, unknown>).altForeignAddressLine2,
      );
      const altBlock = trim(
        (detail as Record<string, unknown>).ALT_BLOCK_NO
        ?? (detail as Record<string, unknown>).altBlockNo,
      );
      const altStreet = trim(
        (detail as Record<string, unknown>).ALT_STREET_NAME
        ?? (detail as Record<string, unknown>).altStreetName,
      );
      const altPostal = trim(
        (detail as Record<string, unknown>).ALT_POSTAL_CODE
        ?? (detail as Record<string, unknown>).altPostalCode,
      );
      if (altLine1) {
        props.model.controllerAlternateAddress = altLine2 ? `${altLine1}, ${altLine2}` : altLine1;
      } else {
        const altLine = [altBlock, altStreet].filter(Boolean).join(' ').trim();
        if (altLine && altPostal) props.model.controllerAlternateAddress = `${altLine}, SINGAPORE ${altPostal}`;
        else if (altLine) props.model.controllerAlternateAddress = altLine;
      }
    }
  } catch (e) {
    console.warn('[NoticeIndividualForm] contact detail fetch failed:', e);
  }
}

const PERSON_LOOKUP_FIELD_GROUPS = new Set(['individualController', 'noticeRecipient']);

watch(
  () => props.selectedPerson,
  (person) => {
    if (!person) return;
    const ctx = props.activePersonContext;
    const fieldGroup = String(ctx?.fieldGroup || '');
    if (fieldGroup === 'noticeSigner') {
      applySelectedSecretary(person);
      return;
    }
    if (!PERSON_LOOKUP_FIELD_GROUPS.has(fieldGroup)) return;
    applySelectedPerson(person);
    void hydrateContactRegNoIfMissing(person);
  },
);

watch(
  () => props.model.noticeRecipientName,
  (name, prev) => {
    const next = trim(name);
    const controller = trim(props.model.controllerFullName);
    const previous = trim(prev);
    if (!next) return;
    if (!controller || controller === previous) {
      props.model.controllerFullName = next;
    }
  },
);

watch(
  () => props.model.controllerFullName,
  (name, prev) => {
    const next = trim(name);
    const recipient = trim(props.model.noticeRecipientName);
    const previous = trim(prev);
    if (!next) return;
    if (!recipient || recipient === previous) {
      props.model.noticeRecipientName = next;
    }
  },
);

watch(
  () => props.model,
  () => {
    if (requiredErrorKeys.value.size === 0) return;
    setRequiredErrors(firstMissingRequiredKeys());
  },
  { deep: true },
);
</script>

<style scoped>
.ad-form {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.ad-section {
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid #e9edf5;
  border-radius: 12px;
  padding: 0 0 12px 0;
  overflow: hidden;
}

.ad-section > :not(.ad-section-header) {
  padding-left: 14px;
  padding-right: 14px;
}

.ad-section-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #283593;
  margin: 0 0 12px 0;
  padding: 10px 14px;
  background: linear-gradient(180deg, #eef2ff 0%, #e8ecfb 100%);
  border-bottom: 1px solid #d6dcf2;
}

.ad-section-docs {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 10px;
  flex-wrap: wrap;
}

.ad-section-docs-label,
.ad-more-chip {
  font-size: 11px;
  color: #888;
}

.ad-doc-chip {
  max-width: 240px;
}

.ad-doc-chip :deep(.v-chip__content) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ad-suggest-chip {
  cursor: pointer;
  font-size: 11px !important;
  background: #ffffff !important;
  color: #212121 !important;
  border-color: #ffb300 !important;
  border-width: 1px !important;
}

.ad-suggest-chip:hover {
  background: #fff8e1 !important;
}

.ad-suggest-chip :deep(.v-chip__content) {
  color: #212121 !important;
}

.ad-suggest-chip :deep(.v-icon) {
  color: #ffb300 !important;
}

.ad-suggest-chip :deep(.v-chip__close) {
  font-size: 13px !important;
  margin-inline-start: 4px;
  opacity: 0.7;
  color: #757575 !important;
}

.ad-suggest-chip :deep(.v-chip__close:hover) {
  opacity: 1;
  color: #212121 !important;
}

.ad-required-mark {
  color: #b3261e;
}

:deep(.ad-required-field.v-input--error .v-label) {
  color: #b3261e !important;
}

.ad-profile-meta-row {
  margin-top: 0;
}
</style>
